import { useState, FC } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Fade, Button, Typography, ButtonGroup } from '@mui/material';
import { Close, RestorePage } from '@mui/icons-material';
import { Modal, Loader } from '../../../components';
import { regenerateTenDayAcceptanceDocs } from '../../../fetch';
import { IBuild } from '../../../models';
import axios, { CancelTokenSource } from 'axios';

interface IRegenerateTenDayAcceptanceModal {
  currentBuild: IBuild;
  open: boolean;
  onClose: () => void;
  fetchBuilds: () => void;
}

export const RegenerateTenDayAcceptanceModal: FC<IRegenerateTenDayAcceptanceModal> = ({
  open,
  onClose,
  currentBuild,
  fetchBuilds
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isGenerating, setGenerating] = useState(false);
  const [generationCancelToken, setGenerationCancelToken] = useState<CancelTokenSource | null>(
    null
  );

  const abortGeneration = () => {
    if (generationCancelToken) {
      generationCancelToken.cancel();
    }
    setGenerationCancelToken(null);
    setGenerating(false);
  };

  const handleClose = () => {
    abortGeneration();
    onClose();
  };

  const generateDocs = async () => {
    setGenerating(true);
    try {
      const cancelTokenSource = axios.CancelToken.source();
      setGenerationCancelToken(cancelTokenSource);
      await regenerateTenDayAcceptanceDocs(currentBuild.buildId, true, cancelTokenSource.token);
      enqueueSnackbar(`10 day acceptance documents Generated!`, {
        variant: 'success'
      });
      handleClose();
      fetchBuilds();
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      enqueueSnackbar(
        error?.Detail ?? 'Error generating Test 10 day acceptance documents, please try again.',
        {
          variant: 'error'
        }
      );
    } finally {
      setGenerating(false);
      setGenerationCancelToken(null);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} maxWidth="sm">
      <Fade in={open}>
        <div>
          {isGenerating && (
            <Loader position="centered" type="overlay">
              Generating...
            </Loader>
          )}
          <Box marginBottom="1rem">
            {currentBuild && (
              <>
                <Typography variant="h5" gutterBottom>
                  Regenerate 10 Day Acceptance Documents
                </Typography>
                <Typography variant="body1">
                  Regenerate the documents that get generated with the 10 day acceptance for{' '}
                  {currentBuild.neighborhood} Phase{' '}
                  {currentBuild.phase ? currentBuild.phase : 'no phase'} Lot{' '}
                  {currentBuild.lotNumber}.
                </Typography>
              </>
            )}
          </Box>
          <Box mt={1} display="flex" alignItems="center" justifyContent="flex-end">
            <ButtonGroup>
              <Button
                color="primary"
                variant="contained"
                disabled={isGenerating}
                startIcon={<RestorePage />}
                onClick={generateDocs}
              >
                Generate
              </Button>
              <Button
                color="inherit"
                variant="contained"
                startIcon={<Close />}
                onClick={handleClose}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </Box>
        </div>
      </Fade>
    </Modal>
  );
};
